import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { endpoint } from "../../../utils/constants/endpoint";
import {
  headersBase,
  headersType,
} from "../../../utils/constants/headersForApi";

export const adminPracticesApi = createApi({
  reducerPath: "adminPractices",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
  }),
  tagTypes: ["practice", "practices", "documents", "alluserdocuments"],
  endpoints: (builder) => ({
    getPracticeManagement: builder.query({
      query: ({
        page,
        userFilter = "",
        filter = {
          status: "",
          serviceId: "",
          studioId: "",
          adminId: "",
          userId: "",
          companyId: "",
          queryName: "",
        },
        orderDirection = "desc",
        valueToOrderBy = "createdAt",
        queryName = "",
      }) => ({
        url: `admin/practices/list?query=${userFilter.trim()}&status=${filter.status
          }&serviceId=${filter.serviceId}&showAll=true&userId=${filter.userId
          }&studioId=${filter.studioId}&adminId=${filter.adminId}&userCompanyId=${filter.companyId
          }&queryName=${queryName}`,
        method: "POST",
        headers: headersType(),
        body: {
          startRow: page.startRow,
          endRow: page.endRow,
          sort: [
            {
              colId: valueToOrderBy,
              sortDirection: orderDirection,
              sortIndex: 0,
            },
          ],
        },
      }),
      providesTags: ["practices"],
    }),
    getAllUserDocuments: builder.query({
      query: ({
        id,
        filter = {
          mainCategoryId: "",
          categoryId: "",
          exclusive: false,
        },
        page,
        query = "",
      }) => ({
        url: `/admin/practices/${id}/alluserdocuments?query=${query}&mainCategoryId=${filter.mainCategoryId}&categoryId=${filter.categoryId}&exclusive=${filter.exclusive}`,
        method: "POST",
        headers: headersType(),
        body: { startRow: page.startRow, endRow: page.endRow },
      }),
      providesTags: ["alluserdocuments", "documents"],
    }),
    getPracticeById: builder.query({
      query: (id) => ({
        url: "admin/practices/" + id,
        method: "GET",
        headers: headersBase(),
      }),
      providesTags: ["practice"],
    }),
    getAdminBookingId: builder.query({
      query: ({ id, userId }) => ({
        url: `admin/Openbox/getBookings/${id}/userId/${userId}`,
        method: "GET",
        headers: headersBase(),
      }),
    }),
    createPractice: builder.mutation({
      query: (practice) => ({
        url: `/admin/practices`,
        method: "POST",
        headers: headersType(),
        body: practice,
      }),
      invalidatesTags: ["practices"],
    }),
    deletePractice: builder.mutation({
      query: (practiceId) => ({
        url: `/admin/practices/${practiceId}`,
        method: "DELETE",
        headers: headersBase(),
      }),
      invalidatesTags: ["practices"],
    }),
    getPracticesServices: builder.query({
      query: () => ({
        url: `${endpoint.ADMIN_PRACTICE_SERVICE.SERVICES}`,
        method: "POST",
        headers: headersType(),
        body: { startRow: 0, endRow: 100 },
      }),
    }),
    getDocuments: builder.query({
      query: (id) => ({
        url: "admin/practices/" + id + "/doccategories",
        method: "GET",
        headers: headersBase(),
      }),
      providesTags: ["documents"],
    }),
    setStatus: builder.mutation({
      query: ({ practiceId, status }) => ({
        url: `/admin/practices/${practiceId}/setstatus?status=${status}`,
        method: "POST",
        headers: headersType(),
        body: { practiceId, status },
      }),
      invalidatesTags: ["practice", "practices"],
    }),
    createPracticeDocument: builder.mutation({
      query: ({ cleanedDocumentData, practiceId }) => ({
        url: `/admin/practices/${practiceId}/newdocument`,
        method: "POST",
        headers: headersType(),
        body: cleanedDocumentData,
      }),
      invalidatesTags: ["documents"],
    }),
    editAdminPractices: builder.mutation({
      query: ({ adminId, practiceId }) => ({
        url: `/admin/practices/${practiceId}/setadmin`,
        method: "POST",
        headers: headersType(),
        body: { adminId },
      }),
      invalidatesTags: ["practice", "practices"],
    }),
    serviceDocComposition: builder.mutation({
      query: (idPratica) => ({
        url: `admin/practices/${idPratica}/doccomposition`,
        method: "POST",
        headers: headersType(),
      }),
      invalidatesTags: [
        "practice",
        "practices",
        "documents",
        "alluserdocuments",
      ],
    }),
    serviceDocCompositionEmail: builder.mutation({
      query: ({ idPratica, level }) => {
        return {
          url: `admin/practices/${idPratica}/doccomposition/email?docSize=${level}`,
          method: "POST",
          headers: headersType(),
        };
      },
      invalidatesTags: [
        "practice",
        "practices",
        "documents",
        "alluserdocuments",
      ],
    }),
    addAssociateDocument: builder.mutation({
      query: (body) => ({
        url: `/admin/practices/${body.id}/documents`,
        method: "POST",
        headers: headersType(),
        body: body,
      }),
      invalidatesTags: [
        "practice",
        "practices",
        "documents",
        "alluserdocuments",
      ],
    }),
    deleteAssociateDocument: builder.mutation({
      query: (params) => ({
        url: `/admin/practices/${params.practiceId}/documents/${params.documentId}`,
        method: "DELETE",
        headers: headersBase(),
        body: params,
      }),
      invalidatesTags: [
        "practice",
        "practices",
        "documents",
        "alluserdocuments",
      ],
    }),
    doRefreshDocs: builder.mutation({
      query: (idPratica) => ({
        url: `admin/practices/${idPratica}/autorefreshdocs`,
        method: "POST",
        headers: headersType(),
        body: idPratica,
      }),
      invalidatesTags: [
        "practice",
        "practices",
        "documents",
        "alluserdocuments",
      ],
    }),
    doRemovePractice: builder.mutation({
      query: (practiceId) => ({
        url: `admin/practices/${practiceId}/removeadmin`,
        method: "POST",
        headers: headersType(),
        body: practiceId,
      }),
      invalidatesTags: ["practices"],
    }),
    doAddNotesPractice: builder.mutation({
      query: ({ notes, practiceId }) => ({
        url: `admin/practices/add-notes/${practiceId}`,
        method: "PUT",
        headers: headersType(),
        body: { notes },
      }),
      invalidatesTags: ["practice", "practices"],
    }),
  }),
});

export const {
  useServiceDocCompositionEmailMutation,
  useGetPracticeManagementQuery,
  useGetPracticesServicesQuery,
  useGetAllUserDocumentsQuery,
  useCreatePracticeMutation,
  useGetPracticeByIdQuery,
  useGetAdminBookingIdQuery,
  useGetDocumentsQuery,
  useSetStatusMutation,
  useCreatePracticeDocumentMutation,
  useDeletePracticeMutation,
  useEditAdminPracticesMutation,
  useServiceDocCompositionMutation,
  useAddAssociateDocumentMutation,
  useDeleteAssociateDocumentMutation,
  useDoRefreshDocsMutation,
  useDoRemovePracticeMutation,
  useDoAddNotesPracticeMutation
} = adminPracticesApi;
